import { computed, onMounted, ref, watch } from "vue";

import { defineStore } from "pinia";

import { CHECKOUT_TEMPLATE_SCHEMA_VERSION } from "@/helpers/constants/checkout";
import { usePromiseWrapper } from "@/hooks/common";
import { useDeals } from "@/hooks/deals";
import { OfferStatus } from "@/models/funders";
import applicationsApiService from "@/services/modules/applications";
import checkoutTemplateService from "@/services/modules/checkoutTemplates";
import optionsApiService from "@/services/modules/options";

import type {
  CheckoutProgress,
  CheckoutSnapshot,
  CheckoutStep,
  CheckoutTemplate,
  CheckoutTemplateBase,
  UpdateCheckoutSnapshotParams,
} from "@/models/checkout";
import type { IResponseMeta } from "@/models/common";
import type { CheckoutOptions } from "@/models/options";

export const useCheckoutStore = defineStore("checkout", () => {
  const { activeDeal, approvedOffers } = useDeals();

  const checkoutTemplates = ref<CheckoutTemplateBase[]>([]);
  const searchMeta = ref<IResponseMeta>();
  const activeTemplate = ref<CheckoutTemplate | null>(null);
  const activeTemplateSelectedStepId = ref<string | null>(null);
  const activeSnapshot = ref<CheckoutSnapshot | null>(null);
  const activeSnapshotProgress = ref<CheckoutProgress | null>(null);
  const unmaskPiisInWfl = ref(true);
  const options = ref<CheckoutOptions>();

  const { fetchWrapper: create } = usePromiseWrapper(
    async (template: CheckoutTemplate) => {
      template.content.version = CHECKOUT_TEMPLATE_SCHEMA_VERSION;

      const newTemplate = await checkoutTemplateService.create(template);
      await getAll();

      return newTemplate;
    },
    {
      onError: (error) => {
        throw error; // Rethrow for validation
      },
    }
  );

  const { fetchWrapper: getAll, loading: getAllIsLoading } = usePromiseWrapper(
    async (params?: Record<string, unknown>) => {
      const { templates, meta } = await checkoutTemplateService.getAll(params);

      searchMeta.value = meta;
      checkoutTemplates.value = templates;
      return templates;
    }
  );

  const { fetchWrapper: getById, loading: getByIdIsLoading } =
    usePromiseWrapper(async (id: string) => {
      return await checkoutTemplateService.getById(id);
    });

  const { fetchWrapper: update } = usePromiseWrapper(
    async (template: CheckoutTemplate & { id: string }) => {
      activeTemplate.value = await checkoutTemplateService.update(template);
      await getAll();
    },
    {
      onError: (error) => {
        throw error; // Rethrow for validation
      },
    }
  );

  const { fetchWrapper: updateSnapshot } = usePromiseWrapper(
    async (updateCheckoutSnapshotParams: UpdateCheckoutSnapshotParams) => {
      // Because updating the active snapshot always returns unmasked piis, we need
      // to reset this value.
      unmaskPiisInWfl.value = !activeSnapshot.value?.content.should_mask_piis;

      activeSnapshot.value =
        await applicationsApiService.updateCheckoutSnapshot(
          updateCheckoutSnapshotParams
        );
    },
    {
      onError: (error) => {
        throw error; // Rethrow for validation
      },
    }
  );

  const { fetchWrapper: deleteById } = usePromiseWrapper(
    async (templateId: string) => {
      await checkoutTemplateService.deleteById(templateId);
      await getAll();

      if (activeTemplate.value?.id === templateId) {
        activeTemplate.value = null;
      }
    }
  );

  const selectedStep = computed({
    get() {
      const stepsToSearch = [
        ...(activeTemplate.value?.content.steps ?? []),
        ...(activeSnapshot.value?.content.steps ?? []),
      ];

      return stepsToSearch.find(
        (step) => step.id === activeTemplateSelectedStepId.value
      );
    },

    set(step: CheckoutStep) {
      if (!activeTemplateSelectedStepId.value) {
        return;
      }

      if (activeTemplate.value) {
        const templateStepIndex = activeTemplate.value?.content.steps.findIndex(
          (step) => step.id === activeTemplateSelectedStepId.value
        );

        if (templateStepIndex > -1) {
          activeTemplate.value.content.steps[templateStepIndex] = step;
        }
      }

      if (activeSnapshot.value) {
        const snapshotStepIndex = activeSnapshot.value?.content.steps.findIndex(
          (step) => step.id === activeTemplateSelectedStepId.value
        );

        if (snapshotStepIndex > -1) {
          activeSnapshot.value.content.steps[snapshotStepIndex] = step;
        }
      }
    },
  });

  const activeTemplateClient = computed(() => {
    const activeTemplateId = activeTemplate.value?.id;

    if (!activeTemplateId) {
      return null;
    }

    return checkoutTemplates.value.find(
      (template) => template.id === activeTemplateId
    )?.client;
  });

  const refreshActiveTemplateProgress = async () => {
    if (!activeSnapshot.value || !activeDeal.value) {
      return;
    }

    const acceptedOffer = approvedOffers.value.find(
      (offer) => offer.status === OfferStatus.Accepted
    );

    if (!acceptedOffer) {
      return;
    }

    activeSnapshotProgress.value =
      await applicationsApiService.getCheckoutProgress(acceptedOffer.id);
  };

  const isActiveTemplateStepComplete = (stepId: string) => {
    return activeSnapshotProgress.value?.step_statuses.some(
      (stepStatus) => stepStatus.is_complete && stepStatus.id === stepId
    );
  };

  watch(
    activeSnapshot,
    async (newActiveSnapshot, oldActiveSnapshot) => {
      if (!newActiveSnapshot) {
        activeSnapshotProgress.value = null;
        return;
      }

      if (newActiveSnapshot.id === oldActiveSnapshot?.id) {
        return;
      }

      await refreshActiveTemplateProgress();

      activeTemplateSelectedStepId.value =
        newActiveSnapshot.content.steps[0].id;
      unmaskPiisInWfl.value = !newActiveSnapshot.content.should_mask_piis;
    },
    { immediate: true }
  );

  onMounted(async () => {
    if (!checkoutTemplates.value.length) {
      await getAll();
    }

    options.value = await optionsApiService.getCheckoutOptions();
  });

  return {
    activeTemplate,
    activeTemplateClient,
    activeTemplateSelectedStepId,
    activeSnapshot,
    activeSnapshotProgress,
    checkoutTemplates,
    getAllIsLoading,
    getByIdIsLoading,
    options,
    searchMeta,
    selectedStep,
    unmaskPiisInWfl,
    create,
    getAll,
    getById,
    update,
    updateSnapshot,
    deleteById,
    isActiveTemplateStepComplete,
    refreshActiveTemplateProgress,
  };
});
